import { useMemo, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import * as Yup from 'yup'
import { FormikProps } from 'formik'

import { useServiceRequest } from '../../hooks/useServiceRequest'
import { actions, selectors } from '../../redux'
import Router from '../../routes/Router'
import routes from '../../routes/definitions'
import { LoginFormProps } from '../../forms/LoginForm'
import { FormFieldOrientation } from '../../types/form'
import useMediaQuery from '../../hooks/useMediaQuery'
import Icon, { Icons } from '../../components/Icon'

const useLoginFormProps = () => {
  const formikRef = useRef<FormikProps<any>>(null)
  const { t } = useTranslation()
  const matches = useMediaQuery('(max-width: 576px)')
  const [isShown, setIsShown] = useState<boolean>(false)

  const [response, login] = useServiceRequest(
    selectors.auth.emailLogin,
    actions.auth.emailLoginRequest,
    actions.auth.emailLoginReset,
    () => formikRef.current?.resetForm()
  )

  const props: LoginFormProps = useMemo(
    () => ({
      innerRef: formikRef,
      validationSchema: Yup.object().shape({
        email: Yup.string().required(t('yup_required')).email(t('yup_email')),
        password: Yup.string().required(t('yup_required')),
      }),
      initialValues: {
        email: '',
        password: '',
      },
      fields: {
        email: {
          placeholder: t('login_form_email_placeholder'),
          label: t('login_form_email'),
          innerError: true,
          required: true,
          ...(matches && {
            orientation: FormFieldOrientation.Vertical,
          }),
        },
        password: {
          label: t('login_form_password'),
          placeholder: t('login_form_password_placeholder'),
          innerError: true,
          required: true,
          ...(matches && {
            orientation: FormFieldOrientation.Vertical,
          }),
          ...(!isShown && {
            rightAdornment: (
              <Icon
                onClick={() => setIsShown((isShown) => !isShown)}
                icon={Icons.eye}
                width={22}
                height={24}
              />
            ),
            type: 'password',
          }),
          ...(isShown && {
            rightAdornment: (
              <Icon
                onClick={() => setIsShown((isShown) => !isShown)}
                icon={Icons.eyeSlash}
                width={22}
                height={24}
              />
            ),
            type: 'text',
          }),
        },
      },
      forgottenButton: {
        href: Router.getRouteUrl(routes.forgottenPassword),
        text: t('login_form_forggoten_password'),
      },
      submitButton: {
        text: t('login_form_login'),
        isLoading: response.pending,
      },
      onSubmit: (values) => {
        login({
          identifier: values.email,
          password: values.password,
        })
      },
      ...(response.errors && {
        errors: [{ label: t('login_error') }],
      }),
    }),
    [t, matches, isShown, response.pending, response.errors, login]
  )

  return props
}

export default useLoginFormProps
